// Google Maps Platform helpers for entering and showing addresses.
//
// CRA bakes REACT_APP_* into the bundle, so the key is public by design and
// must be locked down in Google Cloud: HTTP referrers limited to our domains,
// APIs limited to Maps JavaScript, Places (New) and Maps Embed. With no key
// configured every caller falls back to a plain text address.

export const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();

export const isGoogleMapsEnabled = Boolean(GOOGLE_MAPS_API_KEY);

// Addresses are saved as Google's formatted string, so pin the language —
// otherwise a browser set to another language would save a localized address.
const LANGUAGE = "en";

// Autocomplete searches these first and only looks elsewhere when nothing here
// matches.
export const PRIORITY_REGION_CODES = ["us", "ca"];

const SCRIPT_CALLBACK = "__makaniGoogleMapsReady";

let placesPromise = null;

const loadScript = () =>
  new Promise((resolve, reject) => {
    if (window.google?.maps?.importLibrary) {
      resolve();
      return;
    }

    window[SCRIPT_CALLBACK] = () => {
      delete window[SCRIPT_CALLBACK];
      resolve();
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      v: "weekly",
      language: LANGUAGE,
      loading: "async",
      callback: SCRIPT_CALLBACK,
    })}`;
    script.async = true;
    script.onerror = () => {
      script.remove();
      reject(new Error("Google Maps failed to load"));
    };
    document.head.appendChild(script);
  });

/**
 * Loads the Places library on first use. Resolves to null when no key is
 * configured or the script can't load, so callers just skip autocomplete.
 */
export const loadPlacesLibrary = () => {
  if (!isGoogleMapsEnabled) return Promise.resolve(null);

  if (!placesPromise) {
    placesPromise = loadScript()
      .then(() => window.google.maps.importLibrary("places"))
      .catch(() => {
        // Forget the failure so the next form that opens tries again.
        placesPromise = null;
        return null;
      });
  }
  return placesPromise;
};

// Works without a key — it's a plain link into Google Maps.
export const googleMapsSearchUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export const googleMapsEmbedUrl = (address) =>
  isGoogleMapsEnabled
    ? `https://www.google.com/maps/embed/v1/place?${new URLSearchParams({
        key: GOOGLE_MAPS_API_KEY,
        q: address,
        language: LANGUAGE,
      })}`
    : null;
